import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getErrorMessage } from "@/lib/errorHandler";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import API from "@/lib/api";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  MoreVertical,
  Edit2,
  Trash2,
  UserPlus,
  PauseCircle,
  PlayCircle,
  Search,
  Upload,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function Organizations() {
  const { user, isSuperadmin } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, limit: 50 });

  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);

  // Forms
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    plan: "free",
    max_admins: 1,
    max_quizzes: null,
    max_users: null,
    logo_url: "",
    settings: {},
  });

  const [inviteData, setInviteData] = useState({
    email: "",
    name: "",
  });

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoFileInputRef = useRef(null);

  // Redirect if not superadmin
  useEffect(() => {
    if (!isSuperadmin) {
      window.location.href = "/dashboard";
    }
  }, [isSuperadmin]);

  // Load organizations
  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (searchTerm) params.search = searchTerm;

      const res = await API.get("/superadmin/organizations", { params });
      setOrganizations(res.data.organizations || []);
      setPagination(res.data.pagination || { total: 0, pages: 0, limit: 50 });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load organizations"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperadmin) {
      loadOrganizations();
    }
  }, [isSuperadmin, page, searchTerm]);

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, SVG, or WebP');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);

    try {
      // Delete old logo from S3 if exists
      if (formData.logo_url) {
        try {
          await API.delete('/upload/image', { params: { url: formData.logo_url } });
        } catch (err) {
          console.warn('Failed to delete old logo:', err);
        }
      }

      // Upload new logo
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await API.post('/upload/image?folder_type=logos', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = res.data.url;
      setFormData(prev => ({ ...prev, logo_url: imageUrl }));
      toast.success('Logo uploaded successfully!');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to upload logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Remove logo
  const removeLogo = async () => {
    if (!formData.logo_url) return;

    try {
      await API.delete('/upload/image', { params: { url: formData.logo_url } });
      setFormData(prev => ({ ...prev, logo_url: "" }));
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
      toast.success('Logo removed successfully!');
    } catch (err) {
      console.error('Failed to delete logo:', err);
      setFormData(prev => ({ ...prev, logo_url: "" }));
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
      toast.warning('Logo removed, but may still exist in storage');
    }
  };

  // Create organization
  const handleCreate = async () => {
    try {
      // Generate slug from name if not provided
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, "-");

      await API.post("/superadmin/organizations", {
        ...formData,
        slug,
        max_quizzes: formData.max_quizzes === "" ? null : parseInt(formData.max_quizzes),
        max_users: formData.max_users === "" ? null : parseInt(formData.max_users),
      });

      toast.success("Organization created successfully");
      setCreateDialog(false);
      setFormData({
        name: "",
        slug: "",
        plan: "free",
        max_admins: 1,
        max_quizzes: null,
        max_users: null,
        logo_url: "",
        settings: {},
      });
      loadOrganizations();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to create organization"));
    }
  };

  // Update organization
  const handleUpdate = async () => {
    try {
      await API.put(`/superadmin/organizations/${selectedOrg.id}`, {
        name: formData.name,
        plan: formData.plan,
        max_admins: parseInt(formData.max_admins),
        max_quizzes: formData.max_quizzes === "" ? null : parseInt(formData.max_quizzes),
        max_users: formData.max_users === "" ? null : parseInt(formData.max_users),
        logo_url: formData.logo_url || null,
        // Deliberately NOT sending `settings`: the backend PUT uses
        // exclude_unset, so omitting it leaves org.settings untouched. This
        // dialog edits no settings keys — sending a stale copy seeded from the
        // cached org list would clobber a theme saved on /portal-theme since
        // the list loaded.
      });

      toast.success("Organization updated successfully");
      setEditDialog(false);
      setSelectedOrg(null);
      loadOrganizations();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update organization"));
    }
  };

  // Delete organization
  const handleDelete = async () => {
    try {
      await API.delete(`/superadmin/organizations/${selectedOrg.id}`);
      toast.success("Organization deleted successfully");
      setDeleteDialog(false);
      setSelectedOrg(null);
      loadOrganizations();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete organization"));
    }
  };

  // Toggle organization status
  const handleToggleStatus = async (org) => {
    try {
      await API.patch(`/superadmin/organizations/${org.id}/activate`, {
        is_active: !org.is_active,
      });
      toast.success(
        `Organization ${org.is_active ? "suspended" : "activated"} successfully`
      );
      loadOrganizations();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update organization status"));
    }
  };

  // Invite admin
  const handleInviteAdmin = async () => {
    try {
      await API.post(`/superadmin/organizations/${selectedOrg.id}/invite-admin`, {
        email: inviteData.email,
        name: inviteData.name,
      });

      toast.success("Admin invited successfully");
      setInviteDialog(false);
      setInviteData({ email: "", name: "" });
      setSelectedOrg(null);
      loadOrganizations();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to invite admin"));
    }
  };

  // Note: Filtering now happens server-side

  // Get plan badge color
  const getPlanBadgeVariant = (plan) => {
    switch (plan) {
      case "enterprise":
        return "default";
      case "pro":
        return "secondary";
      case "free":
        return "outline";
      default:
        return "outline";
    }
  };

  if (!isSuperadmin) {
    return null;
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organizations</h1>
            <p className="text-slate-500 mt-1">
              Manage all organizations on the platform
            </p>
          </div>
          <Button onClick={() => setCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Organization
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1); // Reset to first page on search
            }}
            className="pl-10"
          />
        </div>

        {/* Organizations Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Organizations ({pagination.total})</CardTitle>
            <CardDescription>
              View and manage all organizations and their usage statistics
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : organizations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {searchTerm ? "No organizations found" : "No organizations yet"}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Admins</TableHead>
                      <TableHead>Quizzes</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          {org.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {org.slug}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPlanBadgeVariant(org.plan)}>
                          {org.plan}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {org.admin_count || 0}
                        {org.max_admins && ` / ${org.max_admins}`}
                      </TableCell>
                      <TableCell>
                        {org.quiz_count || 0}
                        {org.max_quizzes && ` / ${org.max_quizzes}`}
                      </TableCell>
                      <TableCell>
                        <Badge variant={org.is_active ? "default" : "secondary"}>
                          {org.is_active ? "Active" : "Suspended"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(org.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrg(org);
                                setFormData({
                                  name: org.name,
                                  slug: org.slug,
                                  plan: org.plan,
                                  max_admins: org.max_admins,
                                  max_quizzes: org.max_quizzes || "",
                                  max_users: org.max_users || "",
                                  logo_url: org.logo_url || "",
                                  settings: org.settings || {},
                                });
                                setEditDialog(true);
                              }}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrg(org);
                                setInviteDialog(true);
                              }}
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              Invite Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(org)}>
                              {org.is_active ? (
                                <>
                                  <PauseCircle className="w-4 h-4 mr-2" />
                                  Suspend
                                </>
                              ) : (
                                <>
                                  <PlayCircle className="w-4 h-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrg(org);
                                setDeleteDialog(true);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-slate-600">
                    Showing page {page} of {pagination.pages} ({pagination.total} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                      disabled={page === pagination.pages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Create Organization Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
              <DialogTitle>Create New Organization</DialogTitle>
              <DialogDescription>
                Create a new organization and configure its settings
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5 grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  value={formData.name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Acme Corporation"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="slug">
                  URL Slug * <span className="text-xs text-slate-500">(auto-generated if empty)</span>
                </Label>
                <Input
                  id="slug"
                  value={formData.slug || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                  placeholder="e.g., acme-corp"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="plan">Plan</Label>
                <Select
                  value={formData.plan || "free"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, plan: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="max_admins">Max Admins</Label>
                  <Input
                    id="max_admins"
                    type="number"
                    value={formData.max_admins || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, max_admins: e.target.value })
                    }
                    placeholder="Unlimited"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="max_quizzes">
                    Max Quizzes <span className="text-xs text-slate-500">(empty = unlimited)</span>
                  </Label>
                  <Input
                    id="max_quizzes"
                    type="number"
                    value={formData.max_quizzes || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, max_quizzes: e.target.value })
                    }
                    placeholder="Unlimited"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="max_users">
                    Max Users <span className="text-xs text-slate-500">(empty = unlimited)</span>
                  </Label>
                  <Input
                    id="max_users"
                    type="number"
                    value={formData.max_users || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, max_users: e.target.value })
                    }
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Organization Logo</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Upload a logo image (JPG, PNG, GIF, SVG, WebP - Max 5MB)
                </p>

                {formData.logo_url ? (
                  <div className="relative inline-block max-w-fit">
                    <img
                      src={formData.logo_url}
                      alt="Logo preview"
                      className="h-20 object-contain border border-slate-200 rounded-md p-2 bg-white"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors z-10"
                      title="Remove logo"
                    >
                      ×
                    </button>
                  </div>
                ) : null}

                <div className="flex gap-2">
                  <input
                    ref={logoFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="org-logo-upload-create"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoFileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="flex-1"
                  >
                    {uploadingLogo ? (
                      <>
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {formData.logo_url ? 'Change Logo' : 'Upload Logo'}
                      </>
                    )}
                  </Button>
                </div>

                <Separator className="my-2" />

                <Label htmlFor="logo_url">Or enter logo URL manually</Label>
                <Input
                  id="logo_url"
                  value={formData.logo_url || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, logo_url: e.target.value })
                  }
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>

            <DialogFooter className="px-6 py-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!formData.name}>
                Create Organization
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Organization Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
              <DialogTitle>Edit Organization</DialogTitle>
              <DialogDescription>
                {formData.name || "Update organization settings"}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* ── Profile ─────────────────────────────────────────────── */}
              <section className="space-y-3">
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Profile
                </h4>
                <div className="grid gap-2">
                  <Label htmlFor="edit_name">Organization Name</Label>
                  <Input
                    id="edit_name"
                    value={formData.name || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
              </section>

              <Separator />

              {/* ── Plan & limits ───────────────────────────────────────── */}
              <section className="space-y-3">
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Plan &amp; Limits
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="edit_plan">Plan</Label>
                    <Select
                      value={formData.plan || "free"}
                      onValueChange={(value) =>
                        setFormData({ ...formData, plan: value })
                      }
                    >
                      <SelectTrigger id="edit_plan">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_max_admins">Max Admins</Label>
                    <Input
                      id="edit_max_admins"
                      type="number"
                      value={formData.max_admins || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, max_admins: e.target.value })
                      }
                      placeholder="Unlimited"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_max_quizzes">Max Quizzes</Label>
                    <Input
                      id="edit_max_quizzes"
                      type="number"
                      value={formData.max_quizzes || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, max_quizzes: e.target.value })
                      }
                      placeholder="Unlimited"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="edit_max_users">Max Users</Label>
                    <Input
                      id="edit_max_users"
                      type="number"
                      value={formData.max_users || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, max_users: e.target.value })
                      }
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
              </section>

              <Separator />

              {/* ── Branding ────────────────────────────────────────────── */}
              <section className="space-y-3">
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Branding
                </h4>
                <div className="flex items-start gap-4">
                  {formData.logo_url ? (
                    <div className="relative inline-block max-w-fit shrink-0">
                      <img
                        src={formData.logo_url}
                        alt="Logo preview"
                        className="h-16 max-w-[160px] object-contain border border-slate-200 rounded-md p-2 bg-white"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors z-10"
                        title="Remove logo"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-24 shrink-0 rounded-md border border-dashed border-slate-200 flex items-center justify-center text-[10px] text-slate-400">
                      No logo
                    </div>
                  )}

                  <div className="flex-1 space-y-2">
                    <input
                      ref={logoFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="org-logo-upload-edit"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => logoFileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="w-full"
                    >
                      {uploadingLogo ? (
                        <>
                          <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {formData.logo_url ? 'Change Logo' : 'Upload Logo'}
                        </>
                      )}
                    </Button>
                    <Input
                      id="edit_logo_url"
                      value={formData.logo_url || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, logo_url: e.target.value })
                      }
                      placeholder="Or paste a logo URL"
                    />
                    <p className="text-xs text-slate-500">
                      JPG, PNG, GIF, SVG or WebP — max 5MB.
                    </p>
                  </div>
                </div>
              </section>
            </div>

            <DialogFooter className="px-6 py-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>Update Organization</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Organization Dialog */}
        <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Organization?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{selectedOrg?.name}</strong> and all
                associated data including:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>All admins and users</li>
                  <li>All quizzes and questions</li>
                  <li>All submissions and reports</li>
                  <li>All groups and categories</li>
                </ul>
                <strong className="text-red-600 mt-2 block">
                  This action cannot be undone!
                </strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Organization
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Invite Admin Dialog */}
        <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Admin</DialogTitle>
              <DialogDescription>
                Invite the first admin for <strong>{selectedOrg?.name}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="invite_email">Email *</Label>
                <Input
                  id="invite_email"
                  type="email"
                  value={inviteData.email}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, email: e.target.value })
                  }
                  placeholder="admin@example.com"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="invite_name">Name *</Label>
                <Input
                  id="invite_name"
                  value={inviteData.name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, name: e.target.value })
                  }
                  placeholder="John Doe"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setInviteDialog(false);
                  setInviteData({ email: "", name: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleInviteAdmin}
                disabled={!inviteData.email || !inviteData.name}
              >
                Send Invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
}
